
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface SubscriptionPlan {
  id: string;
  name: string;
  name_en: string;
  description?: string;
  description_en?: string;
  storage_limit_mb: number;
  max_entries?: number;
  max_images?: number;
  price_monthly_cents: number;
  is_default: boolean;
  is_active: boolean;
}

export interface ProfileStorageUsage {
  id: string;
  profile_id: string;
  subscription_plan_id: string;
  storage_used_mb: number;
  entries_count: number;
  images_count: number;
  last_calculated_at?: string;
  subscription_plan?: SubscriptionPlan;
}

export const useProfileStorage = () => {
  const { user } = useAuth();

  // Fetch profile storage usage
  const { data: storageUsage, isLoading: storageLoading, refetch: refetchStorage } = useQuery({
    queryKey: ['profile-storage', user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await supabase
        .from('profile_storage_usage')
        .select(`
          *,
          subscription_plan:subscription_plans(*)
        `)
        .eq('profile_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching storage usage:', error);
        return null;
      }
      
      return data as ProfileStorageUsage & { subscription_plan: SubscriptionPlan } | null;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1
  });

  // Fetch all available subscription plans
  const { data: subscriptionPlans, isLoading: plansLoading } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('price_monthly_cents');

      if (error) {
        console.error('Error fetching subscription plans:', error);
        return [];
      }
      
      return data as SubscriptionPlan[] || [];
    },
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
    retry: 1
  });

  // Calculate storage usage percentage
  const getStoragePercentage = () => {
    if (!storageUsage?.subscription_plan) return 0;
    const used = storageUsage.storage_used_mb;
    const limit = storageUsage.subscription_plan.storage_limit_mb;
    return Math.min((used / limit) * 100, 100);
  };

  // Check if storage is near limit
  const isStorageWarning = () => getStoragePercentage() >= 80;
  const isStorageCritical = () => getStoragePercentage() >= 95;

  // Format storage size
  const formatStorageSize = (mb: number) => {
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(1)} GB`;
    }
    return `${mb.toFixed(0)} MB`;
  };

  // Get plan color based on usage
  const getStorageColor = () => {
    const percentage = getStoragePercentage();
    if (percentage >= 95) return 'destructive';
    if (percentage >= 80) return 'warning';
    return 'default';
  };

  return {
    storageUsage,
    subscriptionPlans: subscriptionPlans || [],
    isLoading: storageLoading || plansLoading,
    refetchStorage,
    getStoragePercentage,
    isStorageWarning: isStorageWarning(),
    isStorageCritical: isStorageCritical(),
    formatStorageSize,
    getStorageColor: getStorageColor()
  };
};
